import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
  "The Practitioner Site — £750",
  "The Care Plan — £39/month",
  "A custom app or web app",
  "A free 20-minute call",
  "Something else",
];

const EMPTY = { name: "", email: "", phone: "", package: "", message: "" };

export default function ContactForm({ open, onOpenChange, defaultPackage }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const pkg = form.package || defaultPackage || "";

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, package: pkg });
      setDone(true);
      toast.success("Got it — Richard will be in touch shortly.");
      setForm(EMPTY);
      setTimeout(() => {
        setDone(false);
        onOpenChange(false);
      }, 2200);
    } catch (err) {
      toast.error("Something went wrong. Please email rich@richgdesign.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="contact-dialog"
        className="max-w-lg bg-cream border-navy/10 rounded-2xl p-0 overflow-hidden"
      >
        <div className="bg-navy px-7 pt-7 pb-6 relative grain overflow-hidden">
          <DialogHeader className="relative z-10 text-left space-y-2">
            <DialogTitle className="font-serif text-2xl text-cream font-medium">
              Let's get you booked.
            </DialogTitle>
            <DialogDescription className="text-cream/70 text-sm">
              Tell me a little about your practice. I reply to every enquiry personally,
              usually within a day.
            </DialogDescription>
          </DialogHeader>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="px-7 py-14 text-center"
            data-testid="contact-success"
          >
            <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-ember/10 flex items-center justify-center">
              <ArrowRight className="h-6 w-6 text-ember" />
            </div>
            <p className="font-serif text-xl text-navy">Message on its way.</p>
            <p className="text-navy/60 text-sm mt-2">Thanks — I'll be in touch soon.</p>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="px-7 py-6 space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="cf-name" className="text-navy/70 text-xs uppercase tracking-wider">
                  Name
                </Label>
                <Input
                  id="cf-name"
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Jane Doe"
                  className="bg-white border-navy/15 focus-visible:ring-ember rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cf-phone" className="text-navy/70 text-xs uppercase tracking-wider">
                  Phone <span className="lowercase text-navy/40">(optional)</span>
                </Label>
                <Input
                  id="cf-phone"
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="07…"
                  className="bg-white border-navy/15 focus-visible:ring-ember rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cf-email" className="text-navy/70 text-xs uppercase tracking-wider">
                Email
              </Label>
              <Input
                id="cf-email"
                type="email"
                data-testid="contact-email-input"
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
                className="bg-white border-navy/15 focus-visible:ring-ember rounded-xl"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-navy/70 text-xs uppercase tracking-wider">
                I'm interested in
              </Label>
              <Select value={pkg} onValueChange={(v) => setForm((f) => ({ ...f, package: v }))}>
                <SelectTrigger
                  data-testid="contact-package-select"
                  className="bg-white border-navy/15 focus:ring-ember rounded-xl"
                >
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent>
                  {PACKAGES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cf-message" className="text-navy/70 text-xs uppercase tracking-wider">
                A little about you
              </Label>
              <Textarea
                id="cf-message"
                data-testid="contact-message-input"
                value={form.message}
                onChange={set("message")}
                rows={4}
                placeholder="What do you do, and what do you need from a website?"
                className="bg-white border-navy/15 focus-visible:ring-ember rounded-xl resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-ember px-6 py-3.5 text-cream font-medium transition-[background-color,transform,box-shadow] hover:bg-ember/90 hover:shadow-lg active:scale-[0.99] disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send enquiry
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
